import { ChangeDetectionStrategy, Component, inject, linkedSignal, signal } from '@angular/core';
import { FormField, form, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { finalize } from 'rxjs';

import { Project } from '../schemas';
import { Messenger } from '../service';
import { ProjectsService } from '../service/projects';

interface BaseData {
  title?: string;
}
interface NewData extends BaseData {
  owner: string;
  project?: never;
}
interface ExistingData extends BaseData {
  owner?: never;
  project: Project;
}

export type ProjectFormData = NewData | ExistingData;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    FormField,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressBarModule,
    MatDatepickerModule,
  ],

  template: `
    <h2 mat-dialog-title>{{ data.title ?? 'Create project' }}</h2>
    <mat-dialog-content>
      <div class="entity-form">
        <mat-form-field>
          <mat-label> Title </mat-label>
          <input [formField]="form.Title" matInput />
        </mat-form-field>

        <mat-form-field>
          <mat-label> Description </mat-label>
          <textarea [formField]="form.Description" matInput></textarea>
        </mat-form-field>

        <mat-form-field>
          <mat-label> Purchase Order </mat-label>
          <input matInput type="number" [formField]="form.PurchaseOrder" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>Enter a date range</mat-label>
          <mat-date-range-input [rangePicker]="rangePicker">
            <input matStartDate required placeholder="Start date" [formField]="form.StartDate" />
            <input matEndDate required placeholder="End date" [formField]="form.EndDate" />
          </mat-date-range-input>
          <mat-hint>DD/MM/YYYY – DD/MM/YYYY</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
          <mat-date-range-picker #rangePicker></mat-date-range-picker>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button matMiniFab matTooltip="Save project" (click)="save()" [disabled]="!form().valid() || pending()">
        <mat-icon>save</mat-icon>
      </button>
    </mat-dialog-actions>
    @if (pending()) {
      <mat-progress-bar mode="indeterminate" />
    }
  `,
})
export class ProjectForm {
  hostDialog = inject(MatDialogRef);
  projects = inject(ProjectsService);
  messenger = inject(Messenger);

  data = inject(MAT_DIALOG_DATA) as ProjectFormData;
  pending = signal(false);

  model = linkedSignal(
    () =>
      ({
        Title: '',
        Owner: this.data.owner,
        Description: '',

        PurchaseOrder: 0,
        StartDate: '',
        EndDate: '',
        ...this.data.project,
      }) as Project,
  );

  form = form(this.model, (schema) => {
    required(schema.Title);
    required(schema.Description);
    required(schema.StartDate);
    required(schema.EndDate);
  });

  save() {
    this.pending.set(true);
    const data = this.form().value();
    this.projects
      .save(data, !!this.data.project)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: (data: Project) => {
          this.hostDialog.close(data);
        },
        error: this.messenger.handleHttpError,
      });
  }
}
